// src/pages/RecurringTasksPage.tsx
// Management page for recurring task templates

import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  useGetTaskTemplatesQuery,
  useCreateTaskTemplateMutation,
  useUpdateTaskTemplateMutation,
  useDeleteTaskTemplateMutation,
  usePauseTaskTemplateMutation,
  useResumeTaskTemplateMutation,
} from '../api/taskTemplatesApi';
import { useGetGoalsQuery } from '../api/goalsApi';
import { useGetContactsQuery } from '../api/contactsApi';
import { useActiveWorkspace } from '../hooks/useActiveWorkspace';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorState from '../components/common/ErrorState';
import Button from '../components/common/Button';
import { DeleteConfirmDialog } from '../components/ui/ConfirmDialog';
import Modal from '../components/ui/Modal';
import RecurrenceFields from '../components/tasks/RecurrenceFields';
import {
  formatRecurrence,
  buildRecurrenceRule,
  recurrenceRuleToState,
  defaultRecurrencePattern,
  type RecurrencePatternState,
} from '../services/recurrence';
import type { TaskTemplate, TaskPriority } from '../types/api';
import { RefreshCw, Pause, Play, Edit2, Trash2, Plus, Calendar, ArrowLeft } from 'lucide-react';

const PRIORITY_CONFIG: Record<TaskPriority, { label: string; color: string }> = {
  low: { label: 'Low', color: 'text-gray-500' },
  medium: { label: 'Medium', color: 'text-yellow-600' },
  high: { label: 'High', color: 'text-orange-600' },
  urgent: { label: 'Urgent', color: 'text-red-600' },
};

const PRIORITIES: TaskPriority[] = ['low', 'medium', 'high', 'urgent'];

interface TemplateFormData {
  title: string;
  description: string;
  priority: TaskPriority;
  relatedContactId: string;
  goalId: string;
  recurrence: RecurrencePatternState;
  startAt: string;
  timezone: string;
}

const initialFormData: TemplateFormData = {
  title: '',
  description: '',
  priority: 'medium',
  relatedContactId: '',
  goalId: '',
  recurrence: { ...defaultRecurrencePattern },
  startAt: new Date().toISOString().slice(0, 16),
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
};

export function RecurringTasksPage() {
  const activeWorkspace = useActiveWorkspace();
  const workspaceId = activeWorkspace?.workspace.id;

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<TaskTemplate | null>(null);
  const [templateToDelete, setTemplateToDelete] = useState<TaskTemplate | null>(null);
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'paused'>('all');
  const [formData, setFormData] = useState<TemplateFormData>(initialFormData);

  const {
    data: templates,
    isLoading,
    error,
    refetch,
  } = useGetTaskTemplatesQuery(
    {
      workspaceId,
      isActive: filterActive === 'all' ? undefined : filterActive === 'active',
    },
    { skip: !workspaceId }
  );

  const { data: goalsData } = useGetGoalsQuery({ workspaceId, status: 'active' }, { skip: !workspaceId });
  const { data: contactsResponse } = useGetContactsQuery({ workspaceId }, { skip: !workspaceId });
  const contacts = contactsResponse?.contacts;

  const [createTemplate, { isLoading: isCreating }] = useCreateTaskTemplateMutation();
  const [updateTemplate, { isLoading: isUpdating }] = useUpdateTaskTemplateMutation();
  const [deleteTemplate] = useDeleteTaskTemplateMutation();
  const [pauseTemplate] = usePauseTaskTemplateMutation();
  const [resumeTemplate] = useResumeTaskTemplateMutation();

  const sortedTemplates = useMemo(() => {
    if (!templates) return [];
    return [...templates].sort((a, b) => {
      // Active first, then by next occurrence
      if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
      if (!a.nextOccurrenceAt) return 1;
      if (!b.nextOccurrenceAt) return -1;
      return new Date(a.nextOccurrenceAt).getTime() - new Date(b.nextOccurrenceAt).getTime();
    });
  }, [templates]);

  const resetForm = () => {
    setFormData(initialFormData);
    setEditingTemplate(null);
  };

  const openEditModal = (template: TaskTemplate) => {
    setEditingTemplate(template);
    setFormData({
      title: template.title,
      description: template.description || '',
      priority: template.priority,
      relatedContactId: template.relatedContactId ?? '',
      goalId: template.goalId ?? '',
      recurrence: recurrenceRuleToState(template.recurrenceRule),
      startAt: template.startAt.slice(0, 16),
      timezone: template.timezone,
    });
    setShowCreateModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!workspaceId) return;

    const recurrenceRule = buildRecurrenceRule(formData.recurrence);

    try {
      if (editingTemplate) {
        await updateTemplate({
          id: editingTemplate.id,
          title: formData.title,
          description: formData.description || undefined,
          priority: formData.priority,
          relatedContactId: formData.relatedContactId || null,
          goalId: formData.goalId || null,
          recurrenceRule,
          startAt: new Date(formData.startAt).toISOString(),
          timezone: formData.timezone,
        }).unwrap();
      } else {
        await createTemplate({
          workspaceId,
          title: formData.title,
          description: formData.description || undefined,
          priority: formData.priority,
          relatedContactId: formData.relatedContactId || null,
          goalId: formData.goalId || null,
          recurrenceRule,
          startAt: new Date(formData.startAt).toISOString(),
          timezone: formData.timezone,
        }).unwrap();
      }
      setShowCreateModal(false);
      resetForm();
    } catch (error) {
      console.error('Failed to save template:', error);
    }
  };

  const handlePauseResume = async (template: TaskTemplate) => {
    try {
      if (template.isActive) {
        await pauseTemplate(template.id).unwrap();
      } else {
        await resumeTemplate(template.id).unwrap();
      }
    } catch (error) {
      console.error('Failed to toggle template status:', error);
    }
  };

  const handleConfirmDelete = async () => {
    if (!templateToDelete) return;
    try {
      await deleteTemplate(templateToDelete.id).unwrap();
      setTemplateToDelete(null);
    } catch (error) {
      console.error('Failed to delete template:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return <ErrorState message="Failed to load recurring tasks." onRetry={refetch} />;
  }

  if (!activeWorkspace) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Please select a workspace to view recurring tasks.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <Link
            to="/tasks"
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-1"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Tasks
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <RefreshCw className="h-6 w-6 text-blue-600" />
            Recurring Tasks
          </h1>
          <p className="text-sm text-gray-500">
            {templates?.length || 0} template{templates?.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Filter */}
          <select
            value={filterActive}
            onChange={(e) => setFilterActive(e.target.value as 'all' | 'active' | 'paused')}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
          >
            <option value="all">All Templates</option>
            <option value="active">Active Only</option>
            <option value="paused">Paused Only</option>
          </select>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="h-4 w-4 mr-1" />
            New Template
          </Button>
        </div>
      </div>

      {/* Explainer */}
      <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-3">
        <p className="text-sm text-blue-700">
          <strong>How it works:</strong> Recurring tasks automatically create task instances for you on a schedule.
          Editing a template won't affect existing tasks — only future ones.
        </p>
      </div>

      {/* Content */}
      {sortedTemplates.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <RefreshCw className="w-8 h-8 text-blue-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">No Recurring Tasks</h3>
          <p className="text-gray-500 mb-4">
            Create a template to automatically generate tasks on a schedule.
          </p>
          <Button onClick={() => setShowCreateModal(true)}>Create Template</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sortedTemplates.map((template) => (
            <div
              key={template.id}
              className={`bg-white rounded-xl border p-4 transition-shadow hover:shadow-md ${
                template.isActive ? 'border-gray-200' : 'border-gray-300 bg-gray-50'
              }`}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate">{template.title}</h3>
                  {template.description && (
                    <p className="text-sm text-gray-500 truncate mt-0.5">{template.description}</p>
                  )}
                </div>
                <span
                  className={`ml-2 px-2 py-0.5 text-xs font-medium rounded-full ${
                    template.isActive
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {template.isActive ? 'Active' : 'Paused'}
                </span>
              </div>

              {/* Details */}
              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm">
                  <RefreshCw className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600">{formatRecurrence(template.recurrenceRule)}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600">
                    Next: {template.nextOccurrenceAt
                      ? new Date(template.nextOccurrenceAt).toLocaleDateString()
                      : 'Not scheduled'}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className={`font-medium ${PRIORITY_CONFIG[template.priority].color}`}>
                    {PRIORITY_CONFIG[template.priority].label} priority
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                <button
                  onClick={() => handlePauseResume(template)}
                  className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                    template.isActive
                      ? 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100'
                      : 'bg-green-50 text-green-700 hover:bg-green-100'
                  }`}
                >
                  {template.isActive ? (
                    <>
                      <Pause className="h-3 w-3" /> Pause
                    </>
                  ) : (
                    <>
                      <Play className="h-3 w-3" /> Resume
                    </>
                  )}
                </button>
                <button
                  onClick={() => openEditModal(template)}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-50 text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <Edit2 className="h-3 w-3" /> Edit
                </button>
                <button
                  onClick={() => setTemplateToDelete(template)}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-red-50 text-red-700 hover:bg-red-100 transition-colors ml-auto"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          resetForm();
        }}
        title={editingTemplate ? 'Edit Recurring Task' : 'New Recurring Task'}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
            <select
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value as TaskPriority })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {PRIORITY_CONFIG[p].label}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Related Contact</label>
              <select
                value={formData.relatedContactId}
                onChange={(e) => setFormData({ ...formData, relatedContactId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">None</option>
                {contacts?.map((contact) => (
                  <option key={contact.id} value={contact.id}>
                    {contact.fullName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Linked Goal</label>
              <select
                value={formData.goalId}
                onChange={(e) => setFormData({ ...formData, goalId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">None</option>
                {goalsData?.goals.map((goal) => (
                  <option key={goal.id} value={goal.id}>
                    {goal.title}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <p className="text-xs text-gray-500 -mt-1">
            Each generated task inherits this contact and goal.
          </p>

          <RecurrenceFields
            value={formData.recurrence}
            onChange={(recurrence) => setFormData({ ...formData, recurrence })}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date & Time</label>
            <input
              type="datetime-local"
              value={formData.startAt}
              onChange={(e) => setFormData({ ...formData, startAt: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setShowCreateModal(false);
                resetForm();
              }}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              isLoading={isCreating || isUpdating}
              className="flex-1"
            >
              {editingTemplate ? 'Save Changes' : 'Create'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <DeleteConfirmDialog
        isOpen={!!templateToDelete}
        itemName={templateToDelete?.title}
        itemType="recurring task template"
        onConfirm={handleConfirmDelete}
        onCancel={() => setTemplateToDelete(null)}
      />
    </div>
  );
}

export default RecurringTasksPage;
